import axios from "../config/axios";
import {
  LoginCredentials,
  AuthResponse,
  OAuthTokenResponse,
  ProfileResponse,
  ValidationResponse,
  User,
} from "../types";

class AuthService {
  private getClientId(): string {
    return process.env.REACT_APP_ADMIN_CLIENT_ID || "admin-console";
  }

  async loginUser(
    credentials: LoginCredentials,
    redirectUrl?: string
  ): Promise<AuthResponse> {
    const response = await axios.post<AuthResponse>(
      "/api/auth/login/user",
      credentials,
      {
        params: redirectUrl ? { redirect: redirectUrl } : undefined,
      }
    );
    return response.data;
  }

  async loginAdmin(
    credentials: LoginCredentials,
    redirectUrl?: string
  ): Promise<AuthResponse> {
    const response = await axios.post<AuthResponse>(
      "/api/auth/login/admin",
      credentials,
      {
        params: {
          ...(redirectUrl ? { redirect: redirectUrl } : {}),
          client_id: this.getClientId(),
        },
      }
    );
    return response.data;
  }

  async validateUser(): Promise<ValidationResponse> {
    const response = await axios.get<ValidationResponse>("/api/auth/validate/user");
    return response.data;
  }

  async validateAdmin(): Promise<ValidationResponse> {
    const response = await axios.get<ValidationResponse>("/api/auth/validate/admin", {
      params: {
        client_id: this.getClientId(),
      },
    });
    return response.data;
  }

  getAuthorizationUrl(): string {
    const authBase = process.env.REACT_APP_AUTH_API || "http://localhost:4100";
    const clientId = this.getClientId();
    const redirectUri =
      process.env.REACT_APP_ADMIN_CALLBACK_URL ||
      `${window.location.origin}/auth/callback`;

    const url = new URL("/api/auth/authorize", authBase);
    url.searchParams.set("client_id", clientId);
    url.searchParams.set("redirect_uri", redirectUri);
    url.searchParams.set("mode", "admin");
    url.searchParams.set("method", "IDPW");
    return url.toString();
  }

  async exchangeAuthorizationCode(code: string): Promise<OAuthTokenResponse> {
    const response = await axios.post<OAuthTokenResponse>("/api/auth/token", {
      code,
      client_id: this.getClientId(),
      redirect_uri:
        process.env.REACT_APP_ADMIN_CALLBACK_URL ||
        `${window.location.origin}/auth/callback`,
    });
    return response.data;
  }

  async logout(): Promise<void> {
    await axios.post("/api/auth/logout");
  }

  async getProfile(): Promise<ProfileResponse> {
    const response = await axios.get<ProfileResponse>("/api/auth/profile");
    return response.data;
  }

  async updateProfile(payload: Record<string, unknown>): Promise<ProfileResponse> {
    const response = await axios.patch<ProfileResponse>("/api/auth/profile", payload);
    return response.data;
  }

  async updateProfileImage(file: File): Promise<{ image: string }> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.put<{ image: string }>("/api/auth/profile/image", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  }

  saveUser(user: User): void {
    localStorage.setItem("sso_user", JSON.stringify(user));
  }

  getUser(): User | null {
    const userStr = localStorage.getItem("sso_user");
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  }

  clearUser(): void {
    localStorage.removeItem("sso_user");
  }

  clearAllData(): void {
    localStorage.clear();
    sessionStorage.clear();

    // Clear cookies
    document.cookie.split(";").forEach((c) => {
      const cookieName = c.replace(/^ +/, "").split("=")[0];
      const expires = `expires=${new Date(0).toUTCString()};path=/`;

      document.cookie = `${cookieName}=;${expires}`;
      document.cookie = `${cookieName}=;${expires};domain=localhost`;
    });
  }
}

const authService = new AuthService();

export default authService;
